import { sortBy } from './array/sortBy.js';
import { deepEquals } from './object/deepEquals.js';
import { objectHas } from './object/objectHas.js';
import { radHttp } from './rad-http/radHttp.js';
import { getStore } from './redux/store/getStore.js';
import { addToast } from './redux/toasts/actions/addToast.js';
import { capitalize } from './string/capitalize.js';
import { meSelector } from '../redux/app/selectors.js';

export const DOWNLOADS_POLL_DELAY = 30 * 1000; // ms

/** @type {Object.<string, DownloadItemStatus>} */
export const DownloadStatus = {
  STARTED: 'Started',
  COMPLETED: 'Completed',
  FAILED: 'Failed'
};

/**
 * @param {DownloadItemType} type
 * @param {boolean} [capitalized=false] If true, returned with the words capitalized.
 */
export const getDownloadTypeString = (type, capitalized = false) => {
  const transform = capitalized ? capitalize : v => v;

  switch (type) {
    case 'AnonymizedImage':
      return transform('anonymized image');
    case 'AnonymizedPatient':
      return transform('anonymized patient');
    case 'NonAnonymizedImage':
      return transform('non-anonymized image');
    case 'NonAnonymizedPatient':
      return transform('non-anonymized patient');
  }

  return transform('download');
}

/**
 * @callback DownloadManager~listener
 * @param {items} DownloadItem[]
 */

/**
 * @typedef DownloadManager~Filter
 * @property {DownloadItemStatus[]|undefined} [statuses]
 * @property {DownloadItemType[]|undefined} [types]
 * @property {boolean|undefined} [hasFile]
 */

export class DownloadManager {
  /** @type {number} */
  _pollTimeoutId;

  /** @type {DownloadManager~listener[]} */
  _listeners = [];

  /** @type {DownloadItem[]|undefined} */
  _items = undefined;

  /** @type {UserInfo} */
  _me;

  /** @type {function()} */
  _unsubscribeStore;

  async enable() {
    this._unsubscribeStore = getStore().subscribe(this._stateChanged);

    await this._poll(true, true, true);
  }

  disable() {
    clearTimeout(this._pollTimeoutId);

    this._unsubscribeStore?.();
    this._unsubscribeStore = undefined;
  }

  /**
   * @param {boolean} [force=false] If true, it will re-poll before returning.
   * @returns {Promise<DownloadItem[]>}
   */
  async get(force = false) {
    if (!this._items || force) {
      await this._poll();
    }

    return this._items;
  }

  /**
   * Returns the current list of items filtered by the options.
   *
   * Defaults to providing only completed items with files.
   * @param {DownloadManager~Filter} [filters]
   *  The statuses to include. Give an empty array or undefined to ignore.
   * @param {boolean} [force=false] If true, it will trigger a poll to get the current items.
   * @returns {Promise<DownloadItem[]>} The filtered items.
   */
  async filtered(filters = {}, force = false) {
    const statuses = objectHas(filters, 'statuses')
      ? filters.statuses
      : [DownloadStatus.COMPLETED];

    const hasFile = objectHas(filters, 'hasFile')
      ? filters.hasFile
      : true;

    const types = objectHas(filters, 'types')
      ? filters.types
      : [];

    return (await this.get(force)).filter(item =>
      (!statuses?.length || statuses?.includes(item.status))
        && (!types?.length || types?.includes(item.type))
        && (hasFile === undefined || item.hasFile === hasFile)
    );
  }

  /**
   * @param {function(items:DownloadItem[])} callback
   */
  addListener(callback) {
    this._listeners.push(callback);
  }

  /**
   * @param {function(items:DownloadItem[])} callback
   */
  removeListener(callback) {
    const index = this._listeners.indexOf(callback);

    if (index === -1) return;

    this._listeners.splice(index, 1);
  }

  /**
   * @param {DownloadItem} item
   * @param {string} [extension='zip']
   */
  async downloadItem(item, extension = 'zip') {
    if (!item) return;

    const response = await radHttp.get(
      `./weekly-check/api/support/download/{organization}/${item.id}`,
      { type: 'blob' }
    );

    if (response?.ok) {
      this.downloadBlob(response.data, `${item.saveId}.${extension}`);
    } else {
      getStore().dispatch(
        addToast(
          `There was a problem downloading the ${getDownloadTypeString(item.type)}.`,
          { color: 'var(--bad-color)' }
        )
      );
    }
  }

  /**
   * @param {string} data
   * @param {string} filename
   * @param {string} mimeType
   */
  downloadText(data, filename = 'download.txt', mimeType = 'text/plain'){
    this._download(`data:${mimeType};charset=utf-8,${encodeURIComponent(data)}`, filename);
  };

  /**
   * @param {Blob} data
   * @param {string} filename
   */
  downloadBlob(data, filename) {
    this._download(window.URL.createObjectURL(data), filename);
  }

  /**
   * @param {Object|string} data
   * @param {string} filename
   */
  downloadJson(data, filename = 'download.json') {
    const downloadStr = typeof data === 'object'
      ? JSON.stringify(data, null, 2)
      : data;

    this.downloadText(downloadStr, filename, 'application/json');
  };

  _download(dataUrl, filename) {
    const element = document.createElement('a');
    element.setAttribute('href', dataUrl);
    element.setAttribute('download', filename);

    element.style.display = 'none';
    document.body.appendChild(element);

    element.click();

    document.body.removeChild(element);
  }

  /**
   * @param {boolean} [loop=false] If true, this will kick off the polling loop.
   * @param {boolean} [notify=false] If true, newly completed items will notify users.
   * @param {boolean} [downloadCompleted=false] If true, any newly completed items will download.
   * @returns {Promise}
   * @private
   */
  async _poll(loop = false, notify = false, downloadCompleted = false) {
    try {
      const response = await radHttp.get('./weekly-check/api/support/downloads/{organization}');

      if (!response.ok) return;

      await this._processItems(response.data, notify, downloadCompleted);

      if (!deepEquals(this._items, response.data)) {
        this._items = response.data.sort(sortBy(['creationDate', -1]));

        for (const listener of this._listeners) {
          listener(this._items);
        }
      }
    } catch(ex) {
      console.error('Error polling downloads', ex);
    }

    if (loop) {
      this._pollTimeoutId =
        setTimeout(() => { this._poll(true, notify, downloadCompleted); }, DOWNLOADS_POLL_DELAY);
    }
  }

  /**
   *
   * @param {boolean} [notify=false] If true, newly completed items will notify users.
   * @param {boolean} [downloadCompleted=false] If true, any newly completed items will download.
   * @param items
   * @param downloadCompleted
   * @returns {Promise<void>}
   * @private
   */
  async _processItems(items, notify = false, downloadCompleted = false) {
    if (!this._me) return;

    // Get all the download items that are finished processing that the user hasn't been notified about
    const newlyProcessedItems = items.filter(item =>
      item.userId === this._me.chartCheckId
      && [DownloadStatus.FAILED, DownloadStatus.COMPLETED].includes(item.status)
      && !item.userNotified
    ).sort(sortBy(['creationDate', -1]));

    if (notify || downloadCompleted) {
      for (const item of newlyProcessedItems) {
        if (item.status === DownloadStatus.COMPLETED) {
          if (notify) {
            getStore().dispatch(
              addToast(
                `Download of ${getDownloadTypeString(item.type)} ready for ${item.lastName}, ${item.firstName} (${item.patientId}). `
                + 'Click show available downloads in the info menu to download.'
              ));
          }

          if (downloadCompleted) {
            await this.downloadItem(item);
          }
        } else if (item.status === DownloadStatus.FAILED) {
          if (notify) {
            getStore().dispatch(
              addToast(
                `Failed to create download for ${item.lastName}, ${item.firstName} (${item.patientId}).`,
                { color: 'var(--bad-color)' }
              ));
          }
        }

        if (notify) {
          await radHttp.put(
            `./weekly-check/api/support/download/{organization}/{itemId}/user-notified`,
            {
              type: 'blob',
              params: { itemId: item.id }
            }
          );
        }
      }
    }
  }

  _stateChanged = state => {
    this._me = meSelector(state);
  };
}

export const downloads = new DownloadManager();